import { GridOverlay } from "@mui/x-data-grid";
import NoInfo from "../assets/SVGs/Table/NoInfo";

export const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

export const filterArrayByString = (mainArr = [], searchText) => {
  if (!searchText.trim()) return mainArr;

  const searchWords = searchText.toLowerCase().split(/\s+/);

  return mainArr.filter((itemObj) =>
    searchWords.every((word) => searchInObj(itemObj, word)),
  );
};

const searchInObj = (itemObj, searchText) => {
  if (!itemObj) {
    return false;
  }

  const propArray = Object.keys(itemObj);

  for (let i = 0; i < propArray.length; i += 1) {
    const prop = propArray[i];
    const value = itemObj[prop];

    if (typeof value === "string") {
      if (searchInString(value, searchText)) {
        return true;
      }
    } else if (Array.isArray(value)) {
      if (searchInArray(value, searchText)) {
        return true;
      }
    }

    if (typeof value === "object") {
      if (searchInObj(value, searchText)) {
        return true;
      }
    }
  }
  return false;
};

const searchInArray = (arr, searchText) => {
  arr.forEach((value) => {
    if (typeof value === "string") {
      if (searchInString(value, searchText)) {
        return true;
      }
    }

    if (typeof value === "object") {
      if (searchInObj(value, searchText)) {
        return true;
      }
    }
    return false;
  });
  return false;
};

const searchInString = (value, searchText) => {
  return value.toLowerCase().includes(searchText);
};

export function CustomNoRowsOverlay(title, description) {
  return (
    <GridOverlay
      sx={{
        padding: "16px 0",
      }}
    >
      <div className="flex flex-col align-center justify-center gap-16">
        <NoInfo />
        <div className="flex flex-col align-center justify-center gap-8">
          <h5
            style={{
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: "600",
              color: "#0F172A",
            }}
          >
            {title}
          </h5>
          <p
            style={{
              fontSize: "14px",
              lineHeight: "20px",
              color: "#475569",
              textAlign: "center",
            }}
          >
            {description}
          </p>
        </div>
      </div>
    </GridOverlay>
  );
}

export const sortByName = (array, key = "referral_name") => {
  if (!Array.isArray(array)) return [];
  return [...array].sort((a, b) => a[key]?.localeCompare(b[key]));
};

export const sortByFullName = (
  array,
  key1 = "first_name",
  key2 = "last_name",
) => {
  if (!Array.isArray(array)) return [];
  return [...array].sort((a, b) =>
    `${a[key1] || ""} ${a[key2] || ""}`.localeCompare(
      `${b[key1] || ""} ${b[key2] || ""}`,
    ),
  );
};

export const formatCurrency = (val) => {
  if (val == null || val === "") return ""; // Handle null/undefined/empty input safely

  // Convert val to a string to prevent `.replace()` errors on numbers
  let numericValue = String(val).replace(/[^0-9.]/g, "");

  // Ensure only two decimal places
  let parts = numericValue.split(".");
  if (parts.length > 2) {
    numericValue = parts[0] + "." + parts.slice(1).join(""); // Remove extra decimals
  }
  if (parts[1]?.length > 2) {
    numericValue = parseFloat(numericValue).toFixed(2);
  }

  // Format with currency style
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(parseFloat(numericValue) || 0);
};

// Members who leave the group are kept as inactive records rather than deleted,
// so historical referrals and closed business keep their real attribution.
// These helpers let the member pickers offer them without hiding the fact that
// they are no longer in the group.
export const isDepartedMember = (member) => member?.status === false;

export const memberFullName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim();

export const CURRENT_MEMBERS_GROUP = "Current members";
export const DEPARTED_MEMBERS_GROUP = "Departed members";

export const memberOptionGroup = (member) =>
  isDepartedMember(member) ? DEPARTED_MEMBERS_GROUP : CURRENT_MEMBERS_GROUP;

// Current members first, each block alphabetical by full name.
export const sortMembersDepartedLast = (array) => {
  if (!Array.isArray(array)) return [];
  return [...array].sort((a, b) => {
    const departedDiff =
      Number(isDepartedMember(a)) - Number(isDepartedMember(b));
    if (departedDiff !== 0) return departedDiff;
    return memberFullName(a).localeCompare(memberFullName(b));
  });
};

export const memberOptionLabel = (member) =>
  isDepartedMember(member)
    ? `${memberFullName(member)} (departed)`
    : memberFullName(member);
